<script setup lang="ts">
import { object } from 'yup'
import type { ClaimStatusResponse, VerificationChannel } from '@/types'

const props = defineProps<{
  clinicSlug: string
  clinicName: string
}>()

const emit = defineEmits<{
  success: []
}>()

const open = defineModel<boolean>('open', { default: false })

const { isAuthenticated } = useAuth()
const { user } = useUser()
const { capture } = useAnalytics()
const { markSuccessModalSeen } = useVetbotPromo()
const { getStatus, startVerification, checkVerification, requestManualClaim, isLoading, error, clearError } = useClaim(props.clinicSlug)

// Modal state
type Step = 'loading' | 'select-phone' | 'enter-code' | 'email-fallback' | 'success' | 'error'
const step = ref<Step>('loading')
const status = ref<ClaimStatusResponse | null>(null)

// Phone selection
const selectedPhone = ref<string | null>(null)

// Verification
const verificationChannel = ref<VerificationChannel | null>(null)
const displayPhone = ref<string>('')

// Step 2: Enter code
const codeSchema = object({ code: verificationCode() })
const codeState = reactive({ code: '' })
const codeFormRef = useTemplateRef('codeForm')

// Step 3: Email fallback
const emailState = reactive({ message: '', consent: false })

// Success message
const successMessage = ref('')

// True only for a completed phone-verified claim (owner now) — gates the vetbot promo
const claimCompleted = ref(false)

// Load status when modal opens
watch(open, async (isOpen) => {
  if (isOpen) {
    await loadStatus()
  } else {
    resetState()
  }
}, { immediate: true })

async function loadStatus() {
  step.value = 'loading'
  clearError()

  const result = await getStatus()
  status.value = result

  if (!result) {
    step.value = 'error'
    return
  }

  // Resume existing PENDING claim — go straight to enter-code
  if (result.existingClaim?.status === 'PENDING' && result.existingClaim.verificationPhone) {
    verificationChannel.value = result.existingClaim.verificationChannel ?? null
    displayPhone.value = result.existingClaim.verificationPhone
    selectedPhone.value = result.existingClaim.verificationPhone
    step.value = 'enter-code'
    return
  }

  if (!result.canClaim) {
    step.value = 'error'
    return
  }

  // Determine initial step based on available phones
  if (result.phones.length > 0) {
    step.value = 'select-phone'
    selectedPhone.value = result.phones[0] ?? null
  } else {
    step.value = 'email-fallback'
  }
}

function resetState() {
  step.value = 'loading'
  status.value = null
  selectedPhone.value = null
  verificationChannel.value = null
  displayPhone.value = ''
  codeState.code = ''
  emailState.message = ''
  emailState.consent = false
  successMessage.value = ''
  claimCompleted.value = false
  clearError()
}

async function handleStartVerification() {
  if (!selectedPhone.value) return

  clearError()
  const result = await startVerification(selectedPhone.value)

  if (result) {
    capture('claim_started', { clinic_name: props.clinicName, method: 'phone' })
    verificationChannel.value = result.channel
    displayPhone.value = selectedPhone.value!
    step.value = 'enter-code'
  }
}

async function handleCheckVerification() {
  try {
    await codeFormRef.value?.validate({})
  } catch {
    return
  }

  clearError()
  const result = await checkVerification(codeState.code)

  if (result?.success) {
    capture('claim_completed', { clinic_name: props.clinicName, method: 'phone' })
    successMessage.value = result.message
    claimCompleted.value = true
    // Prevent the dashboard success modal from re-firing for this user
    markSuccessModalSeen()
    step.value = 'success'
    emit('success')
  }
}

async function handleRequestManual() {
  if (!emailState.consent || !user.value?.email) return

  capture('claim_started', { clinic_name: props.clinicName, method: 'email_fallback' })
  clearError()
  const result = await requestManualClaim(user.value.email, emailState.message || undefined)

  if (result?.success) {
    capture('claim_completed', { clinic_name: props.clinicName, method: 'email_fallback' })
    successMessage.value = result.message
    step.value = 'success'
  }
}

function closeModal() {
  open.value = false
}

async function handleResendCode() {
  if (!selectedPhone.value) return

  clearError()
  codeState.code = ''
  const result = await startVerification(selectedPhone.value)

  if (result) {
    verificationChannel.value = result.channel
    displayPhone.value = selectedPhone.value!
  }
}

function handleBackToPhones() {
  step.value = 'select-phone'
  codeState.code = ''
  clearError()
}

const verificationMessage = computed(() => {
  if (verificationChannel.value === 'sms') {
    return `Odeslali jsme SMS s ověřovacím kódem na číslo ${displayPhone.value}`
  }
  return `Zavoláme vám s ověřovacím kódem na číslo ${displayPhone.value}`
})
</script>

<template>
  <UModal v-model:open="open" :title="step === 'success' ? 'Hotovo' : 'Převzít kliniku'" :ui="{ footer: 'justify-end' }">
    <slot />

    <template #body>
      <!-- Loading state -->
      <div v-if="step === 'loading'" class="flex items-center justify-center py-8">
        <UIcon name="i-lucide-loader-2" class="w-8 h-8 text-primary animate-spin" />
      </div>

      <!-- Error state (can't claim) -->
      <div v-else-if="step === 'error'" class="space-y-4">
        <div class="flex items-start gap-3 p-4 bg-red-50 rounded-lg">
          <UIcon name="i-lucide-alert-circle" class="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div class="text-sm text-red-700">
            {{ status?.reason || error || 'Nepodařilo se načíst informace o klinice' }}
          </div>
        </div>

        <div v-if="!isAuthenticated" class="text-sm text-muted">
          <NuxtLink to="/prihlaseni" class="text-primary hover:underline" @click="closeModal">
            Přihlaste se
          </NuxtLink>
          pro převzetí kliniky.
        </div>
      </div>

      <!-- Step 1: Select phone -->
      <div v-else-if="step === 'select-phone'" class="space-y-4">
        <!-- Single phone -->
        <template v-if="status?.phones.length === 1">
          <p class="text-muted">
            Ověření vlastnictví provedeme skrze telefonní číslo uložené v naší databázi.
          </p>

          <div class="p-3 border border-primary bg-primary/5 rounded-lg">
            <span class="text-highlighted font-medium">{{ status.phones[0] }}</span>
          </div>
        </template>

        <!-- Multiple phones -->
        <template v-else>
          <p class="text-muted">
            Vyberte telefonní číslo, na které vám zašleme ověřovací kód.
          </p>

          <div class="space-y-2">
            <label
              v-for="phone in status?.phones"
              :key="phone"
              class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
              :class="selectedPhone === phone ? 'border-primary bg-primary/5' : 'border-default hover:border-muted'"
            >
              <input
                v-model="selectedPhone"
                type="radio"
                :value="phone"
                class="w-4 h-4 text-primary"
              >
              <span class="text-highlighted">{{ phone }}</span>
            </label>
          </div>
        </template>

        <div v-if="error" class="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
          <UIcon name="i-lucide-alert-circle" class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <span class="text-sm text-red-700">{{ error }}</span>
        </div>

        <p class="text-xs text-dimmed">
          Na mobilní čísla přijde SMS, na pevné linky zavoláme s kódem.
        </p>

        <p class="text-xs text-dimmed">
          Něco není v pořádku?
          <a
            :href="`mailto:podpora@veterina-pohotovost.cz?subject=Problém s ověřením - ${clinicName}`"
            class="underline hover:text-primary transition-colors"
          >Napište nám</a>
        </p>
      </div>

      <!-- Step 2: Enter code -->
      <div v-else-if="step === 'enter-code'" class="space-y-4">
        <p class="text-muted">
          {{ verificationMessage }}
        </p>

        <UForm ref="codeForm" :schema="codeSchema" :state="codeState" :validate-on="[]">
          <UFormField label="Ověřovací kód" name="code">
            <UInput
              v-model="codeState.code"
              placeholder="000000"
              size="lg"
              class="text-center text-2xl tracking-widest font-mono"
              maxlength="6"
              inputmode="numeric"
              autocomplete="one-time-code"
              :ui="{ base: 'text-center' }"
            />
          </UFormField>
        </UForm>

        <div v-if="error" class="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
          <UIcon name="i-lucide-alert-circle" class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <span class="text-sm text-red-700">{{ error }}</span>
        </div>

        <div class="flex items-center gap-3 text-xs text-dimmed">
          <button
            type="button"
            class="underline hover:text-primary transition-colors"
            :disabled="isLoading"
            @click="handleResendCode"
          >
            Odeslat kód znovu
          </button>
          <span class="text-dimmed">|</span>
          <button
            v-if="status?.phones && status.phones.length > 0"
            type="button"
            class="underline hover:text-primary transition-colors"
            :disabled="isLoading"
            @click="handleBackToPhones"
          >
            Změnit číslo
          </button>
          <span v-if="status?.phones && status.phones.length > 0" class="text-dimmed">|</span>
          <a
            :href="`mailto:podpora@veterina-pohotovost.cz?subject=Problém s ověřením - ${clinicName}`"
            class="underline hover:text-primary transition-colors"
          >Napište nám</a>
        </div>
      </div>

      <!-- Email fallback (no phones) -->
      <div v-else-if="step === 'email-fallback'" class="space-y-4">
        <div class="flex items-start gap-3 p-4 bg-amber-50 rounded-lg">
          <UIcon name="i-lucide-info" class="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div class="text-sm text-amber-700">
            Vlastnictví kliniky běžně ověřujeme kódem zaslaným na telefonní číslo kliniky. Tato klinika však nemá v systému žádné telefonní číslo, proto je nutné ruční ověření.
          </div>
        </div>

        <p class="text-muted">
          Budeme vás kontaktovat na <span class="font-medium text-highlighted">{{ user?.email }}</span>
        </p>

        <div>
          <label class="text-sm font-medium text-highlighted">Zpráva (nepovinné)</label>
          <UTextarea
            v-model="emailState.message"
            placeholder="Napište nám, jak můžete dokázat spojení s klinikou..."
            :rows="3"
            class="mt-1 w-full"
          />
        </div>

        <label class="flex items-start gap-2 cursor-pointer">
          <UCheckbox v-model="emailState.consent" class="mt-0.5" />
          <span class="text-sm text-muted">
            Potvrzuji, že jsem oprávněným zástupcem této kliniky a souhlasím s ověřením mého vztahu ke klinice.
          </span>
        </label>

        <div v-if="error" class="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
          <UIcon name="i-lucide-alert-circle" class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <span class="text-sm text-red-700">{{ error }}</span>
        </div>
      </div>

      <!-- Success -->
      <div v-else-if="step === 'success'" class="space-y-4">
        <div class="flex flex-col items-center gap-4 py-4">
          <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <UIcon name="i-lucide-check" class="w-8 h-8 text-green-600" />
          </div>
          <p class="text-center text-muted">
            {{ successMessage }}
          </p>
        </div>
        <div class="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
          <PremiumBadge variant="verified" size="sm" />
          <span class="text-sm text-muted">Váš profil je nyní ověřený</span>
        </div>

        <!-- Vetbot cross-promo reward for completing a claim -->
        <VetbotPromo v-if="claimCompleted" campaign="claim-success" compact />
      </div>
    </template>

    <template #footer>
      <!-- Select phone footer -->
      <template v-if="step === 'select-phone'">
        <UButton
          color="neutral"
          variant="outline"
          label="Zrušit"
          @click="closeModal"
        />
        <UButton
          color="primary"
          label="Odeslat kód"
          :loading="isLoading"
          :disabled="!selectedPhone"
          @click="handleStartVerification"
        />
      </template>

      <!-- Enter code footer -->
      <template v-else-if="step === 'enter-code'">
        <UButton
          color="neutral"
          variant="outline"
          label="Zrušit"
          @click="closeModal"
        />
        <UButton
          color="primary"
          label="Ověřit"
          :loading="isLoading"
          :disabled="codeState.code.length !== 6"
          @click="handleCheckVerification"
        />
      </template>

      <!-- Email fallback footer -->
      <template v-else-if="step === 'email-fallback'">
        <UButton
          color="neutral"
          variant="outline"
          label="Zrušit"
          @click="closeModal"
        />
        <UButton
          color="primary"
          label="Odeslat žádost"
          :loading="isLoading"
          :disabled="!emailState.consent"
          @click="handleRequestManual"
        />
      </template>

      <!-- Success/Error footer -->
      <template v-else-if="step === 'success' || step === 'error'">
        <UButton
          color="neutral"
          :label="step === 'success' ? 'Zavřít' : 'Zrušit'"
          @click="closeModal"
        />
      </template>
    </template>
  </UModal>
</template>
